// pages/journalStock/JournalStock.jsx
import React, { useCallback, useState } from "react";
import { Card, Tag, Popover, message, Modal, Tooltip, Space, Button, Segmented } from "antd";
import { 
    ReloadOutlined, 
    FormOutlined, 
    EyeOutlined, 
    EyeInvisibleOutlined, 
    AppstoreOutlined, 
    GroupOutlined, 
    UnorderedListOutlined
} from '@ant-design/icons';
import { useSelector } from "react-redux";
import FilterJournalStock from "./filterJournalStock/FilterJournalStock";
import useJournalStockData from "./hooks/useJournalStockData";
import JournalHeader from "./journalHeader/JournalHeader";
import Ficheproduit from "./dashbordStock/ficheproduit/FicheProduit";
import CorrectionReceptionModal from "./components/CorrectionReceptionModal";

import config from "../../config";
import canSeeFinance from "../../utils/canSeeFinance";
import { NdoeImage, NdoeResponsiveTable, NdoeDataCard, NdoeError } from '../../components/ndoe';
import { postCorrectionReception } from "../../services/receptionService";
import './journalStock.scss';

const JournalStock = () => {
    const { 
        data, 
        erreur,
        loading, 
        pagination,
        version,
        fetchDatas,
        refreshWithFilters,
        handleTableChange
    } = useJournalStockData();
    
    const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
    const userId = useSelector((state) => state.user?.currentUser.id);
    const user = useSelector((state) => state.user?.currentUser);
    
    const [sortedInfo, setSortedInfo] = useState({});
    const [modalType, setModalType] = useState(null);
    const [id, setId] = useState('');
    const [filterMode, setFilterMode] = useState('dynamique');
    const [viewMode, setViewMode] = useState('variante');
    const [correctionLoading, setCorrectionLoading] = useState(false);
    const [correctionModalVisible, setCorrectionModalVisible] = useState(false);
    const [currentVariant, setCurrentVariant] = useState(null);

    // ============================================
    // GESTION DES MODALES
    // ============================================

    const closeAllModals = () => {
        setModalType(null);
        setCorrectionModalVisible(false);
        setCurrentVariant(null);
    };

    const openModal = (type, id = '') => {
        closeAllModals();
        setModalType(type);
        setId(id);
    };

    // ============================================
    // GESTION DES MODES
    // ============================================

    const handleFilterModeChange = (mode) => {
        setFilterMode(mode);
        
        const showInactifs = {
            'dynamique': 'dynamique',
            'statique': 'statique',
            'tous': 'true'
        }[mode] || 'dynamique';
        
        refreshWithFilters({
            showInactifs: showInactifs,
            groupBy: viewMode
        });
    };

    const handleViewModeChange = (mode) => {
        setViewMode(mode);
        
        const showInactifs = {
            'dynamique': 'dynamique',
            'statique': 'statique',
            'tous': 'true'
        }[filterMode] || 'dynamique';
        
        refreshWithFilters({
            showInactifs: showInactifs,
            groupBy: mode
        });
    };

    // ============================================
    // FILTRES
    // ============================================

    const handleFilter = useCallback((filters) => {
        if (filters.date_debut && filters.date_fin && 
            new Date(filters.date_debut) > new Date(filters.date_fin)) {
            message.error("La date de début doit être antérieure à la date de fin");
            return;
        }
        
        const showInactifs = {
            'dynamique': 'dynamique',
            'statique': 'statique',
            'tous': 'true'
        }[filterMode] || 'dynamique';
        
        refreshWithFilters({
            ...filters,
            showInactifs: showInactifs,
            groupBy: viewMode
        });
    }, [refreshWithFilters, filterMode, viewMode]);

    // ============================================
    // CORRECTION RÉCEPTION
    // ============================================

    const openCorrectionModal = (record) => {
        setCurrentVariant(record);
        setCorrectionModalVisible(true);
    };

    const cancelCorrection = () => {
        setCorrectionModalVisible(false);
        setCurrentVariant(null);
    };

    const handleCorrection = async () => {
        if (!currentVariant || correctionLoading) return;

        let hideLoading = null;

        try {
            setCorrectionLoading(true);
            setCorrectionModalVisible(false);

            hideLoading = message.loading('Correction en cours...', 0);
            const res = await postCorrectionReception(currentVariant.id_varianteProduit, userId);

            if (hideLoading) hideLoading();

            if (res.data.success) {
                message.success(res.data.message || 'Réception corrigée avec succès');
                await fetchDatas();
            } else {
                message.error(res.data.message || 'Erreur lors de la correction');
                if (res.data.data?.reception_existante) {
                    message.warning(
                        `Réception existante: ${res.data.data.reception_existante.quantite} unités`,
                        5
                    );
                }
            }

        } catch (error) {
            if (hideLoading) hideLoading();
            console.error('❌ Erreur handleCorrection:', error);

            if (error.response?.status === 409) {
                message.warning('Une réception initiale existe déjà pour cette variante');
            } else if (error.response?.status === 404) {
                message.error('Variante non trouvée');
            } else if (error.response?.status === 400) {
                message.error(error.response.data?.message || 'Données invalides');
            } else {
                message.error('Erreur lors de la correction. Réessayez.');
            }

        } finally {
            setCorrectionLoading(false);
            setCurrentVariant(null);
        }
    };

    const onTableChange = useCallback((paginationConfig, filters, sorter) => {
        setSortedInfo(sorter);
        handleTableChange(paginationConfig, filters, sorter);
    }, [handleTableChange]);

    const getSortOrder = (columnKey) => {
        if (sortedInfo.columnKey === columnKey) {
            return sortedInfo.order;
        }
        return null;
    };

    const variantColumns = [
        {
            title: "Produit",
            key: "nom_produit",
            fixed: "left",
            width: 320,
            sorter: true,
            sortOrder: getSortOrder('nom_produit'),
            render: (_, record) => (
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <NdoeImage src={record.img} alt={record.nom_produit || 'Article'} size="lg" />
                    <div>
                        <Tooltip title={record.nom_produit || 'Sans nom'}>
                            <div style={{ 
                                fontSize: 14, 
                                fontWeight: 600, 
                                color: "#111827",
                                marginBottom: 4,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: 140
                            }}
                            onClick={() => openModal('detail', record.id_produit)}
                            >
                                {record.nom_produit || 'Sans nom'}
                            </div>
                        </Tooltip>
                        <Space size={4} wrap>
                            {record.marque && (
                                <Tag color="blue" style={{ fontSize: 11, margin: 0 }}>
                                    {record.marque}
                                </Tag>
                            )}
                            {record.categorie && (
                                <Tag style={{ fontSize: 11, margin: 0 }}>
                                    {record.categorie}
                                </Tag>
                            )}
                            {record.taille && (
                                <Tag style={{ fontSize: 11, margin: 0, background: '#f5f5f5' }}>
                                    P {record.taille}
                                </Tag>
                            )}
                            {record.couleur && (
                                <Tag style={{ fontSize: 11, margin: 0, background: '#fafafa' }}>
                                    {record.couleur}
                                </Tag>
                            )}
                            {record.matiere && (
                                <Tag style={{ fontSize: 11, margin: 0, background: '#f0f0f0' }}>
                                    {record.matiere}
                                </Tag>
                            )}
                            <Tag color="purple" style={{ fontSize: 11, margin: 0 }}>
                                {record.code_variant}
                            </Tag>
                        </Space>
                    </div>
                </div>
            ),
        },
        {
            title: "Stock Initial",
            key: "stock_initial",
            dataIndex: "stock_initial",
            align: "center",
            width: 100,
            sorter: true,
            sortOrder: getSortOrder('stock_initial'),
            render: (value) => (
                <span style={{ fontWeight: 600, color: '#595959' }}>
                    {value || 0}
                </span>
            ),
        },
        {
            title: "Entrées",
            key: "entrees_periode",
            dataIndex: "entrees_periode",
            align: "center",
            width: 90,
            sorter: true,
            sortOrder: getSortOrder('entrees_periode'),
            render: (value) => (
                <Tag color="success" style={{ fontWeight: 600, minWidth: 50, textAlign: "center" }}>
                    +{value || 0}
                </Tag>
            ),
        },
        {
            title: "Sorties",
            key: "sorties_physiques_periode",
            dataIndex: "sorties_physiques_periode",
            align: "center",
            width: 90,
            sorter: true,
            sortOrder: getSortOrder('sorties_physiques_periode'),
            render: (value) => (
                <Tag color="error" style={{ fontWeight: 600, minWidth: 50, textAlign: "center" }}>
                    -{value || 0}
                </Tag>
            ),
        },
        {
            title: "Stock Final",
            key: "stock_final",
            dataIndex: "stock_final",
            align: "center",
            width: 100,
            sorter: true,
            sortOrder: getSortOrder('stock_final'),
            render: (value, record) => {
                let color = 'success';
                let label = 'Disponible';
                
                if (value === 0) {
                    color = 'error';
                    label = 'Rupture';
                } else if (value <= 5) {
                    color = 'warning';
                    label = 'Stock faible';
                } else if (record.statut === 'dormant') {
                    color = 'default';
                    label = 'Dormant';
                }
                
                return (
                    <Tooltip title={`${label} - ${value} unités`}>
                        <Tag color={color} style={{ fontWeight: 700, minWidth: 60, textAlign: "center" }}>
                            {value}
                        </Tag>
                    </Tooltip>
                );
            },
        },
/*         {
            title: "Ventes Nettes",
            key: "ventes_nettes_periode",
            dataIndex: "ventes_nettes_periode",
            align: "center",
            width: 100,
            sorter: true,
            sortOrder: getSortOrder('ventes_nettes_periode'),
            render: (value) => (
                <span style={{ fontWeight: 500, color: '#1677ff' }}>
                    {value || 0}
                </span>
            ),
        }, */
        {
            title: "CA Brut",
            key: "ca_brut_periode",
            dataIndex: "ca_brut_periode",
            align: "right",
            width: 110,
            sorter: true,
            sortOrder: getSortOrder('ca_brut_periode'),
            render: (value) => (
                <span style={{ fontWeight: 700, color: "#1677ff" }}>
                    {Number(value || 0).toLocaleString()} $
                </span>
            ),
        },
/*         {
            title: "Taux",
            key: "taux_ecoulement",
            dataIndex: "taux_ecoulement",
            align: "center",
            width: 90,
            sorter: true,
            sortOrder: getSortOrder('taux_ecoulement'),
            render: (value) => {
                const color = value > 70 ? 'green' : value > 40 ? 'orange' : 'red';
                return (
                    <Tag color={color} style={{ fontWeight: 600, minWidth: 50, textAlign: "center" }}>
                        {value || 0}%
                    </Tag>
                );
            },
        }, */
        {
            title: "Jours",
            key: "jours_stock",
            dataIndex: "jours_stock",
            align: "center",
            width: 80,
            sorter: true,
            sortOrder: getSortOrder('jours_stock'),
            render: (value) => (
                <span style={{ 
                    fontWeight: 500, 
                    color: value > 90 ? '#ff4d4f' : value > 45 ? '#faad14' : '#52c41a' 
                }}>
                    {value || 0}j
                </span>
            ),
        },
        {
            title: "Statut",
            key: "statut",
            dataIndex: "statut",
            align: "center",
            width: 110,
            filters: [
                { text: 'Actif', value: 'actif' },
                { text: 'Alerte', value: 'alerte' },
                { text: 'Rupture', value: 'rupture' },
                { text: 'Dormant', value: 'dormant' },
            ],
            onFilter: (value, record) => record.statut === value,
            render: (value) => {
                const config = {
                    'actif': { color: 'green', label: 'Actif', icon: '●' },
                    'alerte': { color: 'orange', label: 'Alerte', icon: '⚠' },
                    'rupture': { color: 'red', label: 'Rupture', icon: '✕' },
                    'dormant': { color: 'default', label: 'Dormant', icon: '○' }
                };
                const { color, label, icon } = config[value] || config['actif'];
                return (
                    <Tag color={color} style={{ fontWeight: 600, minWidth: 70, textAlign: "center" }}>
                        {icon} {label}
                    </Tag>
                );
            },
        },
        {
            title: 'Action',
            key: 'action',
            width: 50,
            render: (text, record) => (
                <Popover 
                    title="Corriger la réception"
                    content="Insérer la réception initiale"
                    trigger="hover"
                >
                    <Button 
                        icon={<FormOutlined />} 
                        style={{ color: 'green' }}
                        onClick={() => openCorrectionModal(record)}
                        loading={correctionLoading}
                        size="small"
                    />
                </Popover>
            )
        }
    ];

    const productColumns = [
        {
            title: "Produit",
            key: "nom_produit",
            fixed: "left",
            width: 300,
            render: (_, record) => (
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <NdoeImage src={record.img} alt={record.nom_produit || 'Article'} size="lg" />
                    <div>
                        <Tooltip title={record.nom_produit || 'Sans nom'}>
                            <div style={{ 
                                fontSize: 14, 
                                fontWeight: 600, 
                                color: "#111827",
                                marginBottom: 4,
                            }}
                            onClick={() => openModal('detail', record.id_produit)}
                            >
                                {record.nom_produit || 'Sans nom'}
                            </div>
                        </Tooltip>
                        <Space size={4} wrap>
                            {record.marque && (
                                <Tag color="blue" style={{ fontSize: 11, margin: 0 }}>
                                    {record.marque}
                                </Tag>
                            )}
                            {record.categorie && (
                                <Tag style={{ fontSize: 11, margin: 0 }}>
                                    {record.categorie}
                                </Tag>
                            )}
                            <Tag style={{ fontSize: 11, margin: 0, background: '#fafafa' }}>
                                {record.nb_variantes || 0} variante(s)
                            </Tag>
                        </Space>
                    </div>
                </div>
            ),
        },
        {
            title: "Stock Total",
            key: "stock_total",
            dataIndex: "stock_total",
            align: "center",
            width: 100,
            sorter: true,
            sortOrder: getSortOrder('stock_total'),
            render: (value) => (
                <span style={{ fontWeight: 600, color: '#1677ff' }}>
                    {value || 0}
                </span>
            ),
        },
        {
            title: "Stock Initial",
            key: "stock_initial_total",
            dataIndex: "stock_initial_total",
            align: "center",
            width: 100,
            sorter: true,
            sortOrder: getSortOrder('stock_initial_total'),
            render: (value) => (
                <span style={{ fontWeight: 600, color: '#595959' }}>
                    {value || 0}
                </span>
            ),
        },
        {
            title: "Entrées",
            key: "entrees_total",
            dataIndex: "entrees_total",
            align: "center",
            width: 90,
            sorter: true,
            sortOrder: getSortOrder('entrees_total'),
            render: (value) => (
                <Tag color="success" style={{ fontWeight: 600, minWidth: 50, textAlign: "center" }}>
                    +{value || 0}
                </Tag>
            ),
        },
        {
            title: "Sorties",
            key: "sorties_total",
            dataIndex: "sorties_total",
            align: "center",
            width: 90,
            sorter: true,
            sortOrder: getSortOrder('sorties_total'),
            render: (value) => (
                <Tag color="error" style={{ fontWeight: 600, minWidth: 50, textAlign: "center" }}>
                    -{value || 0}
                </Tag>
            ),
        },
        {
            title: "Ventes Nettes",
            key: "ventes_nettes_total",
            dataIndex: "ventes_nettes_total",
            align: "center",
            width: 100,
            sorter: true,
            sortOrder: getSortOrder('ventes_nettes_total'),
            render: (value) => (
                <span style={{ fontWeight: 500, color: '#1677ff' }}>
                    {value || 0}
                </span>
            ),
        },
        {
            title: "CA Total",
            key: "ca_total",
            dataIndex: "ca_total",
            align: "right",
            width: 130,
            sorter: true,
            sortOrder: getSortOrder('ca_total'),
            render: (value) => (
                <span style={{ fontWeight: 700, color: "#1677ff" }}>
                    {Number(value || 0).toLocaleString()} $
                </span>
            ),
        },
        {
            title: "Variantes",
            key: "nb_variantes",
            dataIndex: "nb_variantes",
            align: "center",
            width: 90,
            sorter: true,
            sortOrder: getSortOrder('nb_variantes'),
            render: (value) => (
                <Tag color="purple">
                    {value || 0}
                </Tag>
            ),
        },
        {
            title: "Statut",
            key: "statut",
            dataIndex: "statut",
            align: "center",
            width: 110,
            sorter: true,
            sortOrder: getSortOrder('statut'),
            render: (value) => {
                const config = {
                    'actif': { color: 'green', label: 'Actif', icon: '●' },
                    'alerte': { color: 'orange', label: 'Alerte', icon: '⚠' },
                    'rupture': { color: 'red', label: 'Rupture', icon: '✕' },
                    'dormant': { color: 'default', label: 'Dormant', icon: '○' }
                };
                const { color, label, icon } = config[value] || config['actif'];
                return (
                    <Tag color={color} style={{ fontWeight: 600, minWidth: 70, textAlign: "center" }}>
                        {icon} {label}
                    </Tag>
                );
            },
        }
    ];

    const baseColumns = viewMode === 'variante' ? variantColumns : productColumns;
    const currentColumns = canSeeFinance(user)
        ? baseColumns
        : baseColumns.filter((col) => col.key !== 'ca_brut_periode' && col.key !== 'ca_total');
    const rowKey = viewMode === 'variante' ? 'id_varianteProduit' : 'id_produit';

    // Carte mobile du journal de stock. Elle doit montrer EXACTEMENT ce que le
    // desktop montre, ni plus ni moins :
    //  - même filtre finance que les colonnes ci-dessus (canSeeFinance), sinon
    //    un utilisateur sans droit verrait sur son téléphone le CA qu'on lui
    //    cache sur desktop ;
    //  - « Ventes nettes » suit les colonnes : commentée en mode variante
    //    (l.333), active en mode produit (l.542). L'afficher quand même en
    //    variante ferait apparaître « 0 » en permanence — un chiffre faux, pire
    //    qu'une donnée absente.
    // À savoir : le tri et le filtre Statut n'existent pas sur cette liste ;
    // ils restent sur la vue desktop.
    const renderCarteStock = (record) => {
        const v = viewMode === 'variante';
        const metrics = [
            { label: 'Stock initial', value: (v ? record.stock_initial : record.stock_initial_total) || 0 },
            { label: 'Entrées', value: (v ? record.entrees_periode : record.entrees_total) || 0 },
            { label: 'Sorties', value: (v ? record.sorties_physiques_periode : record.sorties_total) || 0 },
            v
                ? { label: 'Stock final', value: record.stock_final || 0 }
                : { label: 'Stock total', value: record.stock_total || 0 },
        ];
        if (v) metrics.push({ label: 'Jours de stock', value: record.jours_stock ?? '—' });
        else {
            metrics.push({ label: 'Ventes nettes', value: record.ventes_nettes_total || 0 });
            metrics.push({ label: 'Variantes', value: record.nb_variantes || 0 });
        }
        if (canSeeFinance(user)) {
            metrics.push({
                label: v ? 'CA brut' : 'CA total',
                value: `${Number((v ? record.ca_brut_periode : record.ca_total) || 0).toLocaleString()} $`,
            });
        }

        const st = {
            actif: { color: 'green', label: 'Actif', icon: '●' },
            alerte: { color: 'orange', label: 'Alerte', icon: '⚠' },
            rupture: { color: 'red', label: 'Rupture', icon: '✕' },
            dormant: { color: 'default', label: 'Dormant', icon: '○' },
        }[record.statut] || { color: 'green', label: 'Actif', icon: '●' };

        return (
            <NdoeDataCard
                media={<NdoeImage src={record.img} alt={record.nom_produit || 'Article'} size="lg" />}
                title={record.nom_produit || 'Sans nom'}
                subtitle={
                    <Space size={4} wrap>
                        {record.marque && <Tag color="blue" style={{ margin: 0 }}>{record.marque}</Tag>}
                        {record.taille && <Tag style={{ margin: 0 }}>P {record.taille}</Tag>}
                        {record.couleur && <Tag style={{ margin: 0 }}>{record.couleur}</Tag>}
                        {record.code_variant && <Tag color="purple" style={{ margin: 0 }}>{record.code_variant}</Tag>}
                    </Space>
                }
                badge={<Tag color={st.color} style={{ fontWeight: 600, margin: 0 }}>{st.icon} {st.label}</Tag>}
                metrics={metrics}
                onClick={() => openModal('detail', record.id_produit)}
                actions={
                    <Button
                        icon={<FormOutlined />}
                        onClick={(e) => { e.stopPropagation(); openCorrectionModal(record); }}
                        loading={correctionLoading}
                    >
                        Corriger la réception
                    </Button>
                }
            />
        );
    };

    return (
        <div className="page">
            <JournalHeader title="Journal de stock global" />
            <div className="page-content">
                <FilterJournalStock onSearch={handleFilter} />

                <Card
                    bordered={false}
                    style={{
                        borderRadius: 20,
                        overflow: "hidden",
                        boxShadow: "0 8px 30px rgba(15,23,42,0.08)",
                    }}
                    extra={
                        <Space size={16} wrap>
                            <Segmented
                                value={filterMode}
                                onChange={handleFilterModeChange}
                                options={[
                                    { 
                                        label: (
                                            <Space size={4}>
                                                <EyeOutlined style={{ color: '#52c41a' }} />
                                                <span>Dynamique</span>
                                            </Space>
                                        ),
                                        value: 'dynamique'
                                    },
                                    { 
                                        label: (
                                            <Space size={4}>
                                                <EyeInvisibleOutlined style={{ color: '#faad14' }} />
                                                <span>Statique</span>
                                            </Space>
                                        ),
                                        value: 'statique'
                                    },
                                    { 
                                        label: (
                                            <Space size={4}>
                                                <AppstoreOutlined style={{ color: '#1677ff' }} />
                                                <span>Tous</span>
                                            </Space>
                                        ),
                                        value: 'tous'
                                    },
                                ]}
                                size="middle"
                            />

                            <Segmented
                                value={viewMode}
                                onChange={handleViewModeChange}
                                options={[
                                    { 
                                        label: (
                                            <Space size={4}>
                                                <UnorderedListOutlined />
                                                <span>Liste</span>
                                            </Space>
                                        ),
                                        value: 'variante'
                                    },
                                    { 
                                        label: (
                                            <Space size={4}>
                                                <GroupOutlined />
                                                <span>Groupe</span>
                                            </Space>
                                        ),
                                        value: 'produit'
                                    },
                                ]}
                                size="middle"
                            />

                            <Button 
                                icon={<ReloadOutlined />} 
                                onClick={() => {
                                    const showInactifs = {
                                        'dynamique': 'dynamique',
                                        'statique': 'statique',
                                        'tous': 'true'
                                    }[filterMode] || 'dynamique';
                                    refreshWithFilters({ 
                                        showInactifs,
                                        groupBy: viewMode
                                    });
                                }}
                                loading={loading}
                            >
                                Actualiser
                            </Button>
                        </Space>
                    }
                >
                    {/* Sur téléphone : liste de cartes, plus de table de 10 colonnes
                        étirée sur 1400px. Le scroll x:1400 ne vaut plus que pour
                        le desktop, où il a du sens.
                        Carte écrite à la main plutôt que déduite des colonnes :
                        en mode variante, le render de « Produit » est taillé pour
                        sa colonne de 320px (maxWidth:140 + ellipsis, l.202/218)
                        et tronquerait le nom dans une carte deux fois plus large. */}
                    {/* UNE PANNE N'EST PAS UN STOCK VIDE. Sans ce bandeau,
                        l'écran affichait « Aucun article — Ajustez la recherche »
                        pendant une panne réseau : la propriétaire pouvait croire
                        son stock vide. On garde la liste affichée (elle porte
                        peut-être les dernières données connues) et on dit
                        clairement qu'elle n'a pas pu être rafraîchie. */}
                    {erreur && (
                        <NdoeError
                            title="Le journal n’a pas pu être rafraîchi"
                            text={`${erreur} Les chiffres affichés peuvent dater.`}
                            onRetry={() => fetchDatas()}
                        />
                    )}
                    <NdoeResponsiveTable
                        key={`${viewMode}-${version}`}
                        loading={loading}
                        columns={currentColumns}
                        data={data || []}
                        rowKey={rowKey}
                        renderCard={renderCarteStock}
                        scroll={{ x: 1400 }}
                        onChange={onTableChange}
                        empty={{ icon: '📦', title: 'Aucun article', text: 'Ajustez la recherche ou les filtres.' }}
                        pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            pageSizeOptions: ['10', '20', '50', '100', '200'],
                            showTotal: (total) =>
                                `${total.toLocaleString()} ${viewMode === 'variante' ? 'variante(s)' : 'produit(s)'}`,
                        }}
                    />
                </Card>
            </div>

            <CorrectionReceptionModal
                visible={correctionModalVisible}
                variant={currentVariant}
                userId={userId}
                onConfirm={handleCorrection}
                onCancel={cancelCorrection}
            />

            <Modal
                title=""
                open={modalType === 'detail'}
                onCancel={closeAllModals}
                footer={null}
                width={1250}
                centered
            >
                <Ficheproduit id_produit={id} />
            </Modal>
        </div>
    );
};

export default JournalStock;